import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  LocalShipping as TruckIcon,
  Badge as BadgeIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import * as customerServices from "../../services/customer";
import * as salengServices from "../../services/saleng";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";

const { v4: uuidv4 } = require("uuid");

const PASTEL_COLORS = [
  "bg-blue-100 text-blue-600",
  "bg-green-100 text-green-600",
  "bg-purple-100 text-purple-600",
  "bg-orange-100 text-orange-600",
  "bg-pink-100 text-pink-600",
  "bg-indigo-100 text-indigo-600",
  "bg-teal-100 text-teal-600",
  "bg-cyan-100 text-cyan-600",
];

const getRandomColor = (id) => {
  let hash = 0;
  const str = String(id || "default");
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % PASTEL_COLORS.length;
  return PASTEL_COLORS[index];
};

/** ------------------------------
 * Shipping Dialog (Modernized with Tailwind)
 * ------------------------------*/
function ShippingDialog({ open, onClose, onSubmit, customer }) {
  const [shippingName, setShippingName] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // เลือกคันรถ + คนขับ (สามารถกดย้ำเพื่อเอาออกได้)
  const [selectedCarId, setSelectedCarId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const [carSelectError, setCarSelectError] = useState(false);

  const cars = Array.isArray(customer?.cars) ? customer.cars : [];
  const drivers = Array.isArray(customer?.drivers) ? customer.drivers : [];

  // รถไม่จำเป็นต้องเลือก (optional)
  const mustPickCar = false;

  useEffect(() => {
    if (!open) return;
    // reset ทุกครั้งที่เปิด dialog
    setShippingName("");
    setPoNumber("");
    setDriverName("");
    setPhoneNumber(customer?.phone || "");
    setNote("");

    setSelectedCarId("");
    setSelectedDriverId("");
    setCarSelectError(false);

    setVehicleNumber("");
  }, [open, customer]);

  // --- Toggle เลือกรถ: กดย้ำที่คันเดิม = เอารถออก + ล้างคนขับ + ล้างทะเบียน
  const toggleCar = (id) => {
    const idStr = String(id);
    if (String(selectedCarId) === idStr) {
      // เอารถออก
      setSelectedCarId("");
      setSelectedDriverId(""); // ถ้าเอารถออก คนขับต้องออกด้วย
      setVehicleNumber("");
    } else {
      // เลือกรถใหม่
      setSelectedCarId(idStr);
      setCarSelectError(false);
      setSelectedDriverId(""); // เปลี่ยนรถ => ล้างคนขับ
      const car = cars.find((c) => String(c.id) === idStr);
      setVehicleNumber(car?.license || "");
    }
  };

  // --- Toggle เลือกคนขับ: กดย้ำที่คนเดิม = เอาคนขับออก + ล้างชื่อ
  const toggleDriver = (id) => {
    const idStr = String(id);
    if (String(selectedDriverId) === idStr) {
      // เอาคนขับออก
      setSelectedDriverId("");
      setDriverName("");
    } else {
      // เลือกคนขับใหม่
      setSelectedDriverId(idStr);
      const drv = drivers.find((d) => String(d.id) === idStr);
      const full = [drv?.name, drv?.surname].filter(Boolean).join(" ").trim();
      setDriverName(full || "");
    }
  };

  const handleSubmit = async () => {
    if (mustPickCar && !selectedCarId) {
      setCarSelectError(true);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        shippingName,
        purchaseOrderNumber: poNumber,
        driverName,
        vehicleNumber,
        phoneNumber,
        note,
        carId: selectedCarId ? Number(selectedCarId) : undefined,
        driverId: selectedDriverId ? Number(selectedDriverId) : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const personTypeLabel =
    customer?.personType === "corporate" ? "นิติบุคคล" : "บุคคลธรรมดา";

  const canSubmit = !submitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div className="bg-white px-6 py-5 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="w-2 h-8 bg-[#10B981] rounded-full mr-2"></span>
            สร้างรายการขนส่ง
          </h2>
          {customer && (
            <p className="text-sm text-gray-500 ml-6 mt-1">
              กำลังทำรายการให้กับ: <span className="font-semibold text-gray-700">{customer.name}</span>
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <div className="flex flex-col md:flex-row h-full">
          {/* Left Panel: Customer, Cars, Drivers */}
          <div className="w-full md:w-5/12 bg-gray-50/50 p-6 border-r border-gray-100 overflow-y-auto max-h-[70vh]">
            <div className="space-y-6">

              {/* Customer Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 relative overflow-hidden group hover:border-[#10B981]/30 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#10B981]/10 to-transparent rounded-bl-full -mr-8 -mt-8 opacity-50 group-hover:scale-110 transition-transform"></div>

                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">ลูกค้า</span>
                      <h3 className="text-lg font-bold text-gray-800 mt-1 leading-tight">{customer?.name || "-"}</h3>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-600 text-[10px] font-medium border border-gray-200">
                        {personTypeLabel}
                      </span>
                      {customer?.customerType && (
                        <span className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${customer.customerType === "SELLER"
                            ? "bg-orange-50 text-orange-600 border-orange-100"
                            : "bg-blue-50 text-blue-600 border-blue-100"
                          }`}>
                          {customer.customerType === "SELLER" ? "SELLER" : customer.customerType}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 mt-4">
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-400">เบอร์โทรศัพท์</p>
                        <p className="font-medium text-gray-700">{customer?.phone || "-"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-400">ที่อยู่</p>
                        <p className="font-medium text-gray-700 leading-relaxed">{customer?.address || "-"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cars Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                    <TruckIcon sx={{ fontSize: 18 }} className="text-gray-400" />
                    รถที่ลงทะเบียนไว้
                  </h4>
                  <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {cars.length ? "เลือกหรือไม่ก็ได้" : "ไม่มีข้อมูล"}
                  </span>
                </div>

                {cars.length > 0 ? (
                  <div className="grid gap-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
                    {cars.map((car) => {
                      const isSelected = String(selectedCarId) === String(car.id);
                      return (
                        <div
                          key={car.id}
                          onClick={() => toggleCar(car.id)}
                          className={`
                             relative p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3
                             ${isSelected
                              ? "border-[#10B981] bg-[#10B981]/5 shadow-md"
                              : "border-transparent bg-white shadow-sm hover:border-[#10B981]/30 hover:shadow-md"
                            }
                           `}
                        >
                          <div className={`
                              w-10 h-10 rounded-lg flex items-center justify-center transition-colors
                              ${isSelected ? "bg-[#10B981] text-white" : "bg-gray-100 text-gray-400"}
                           `}>
                            <TruckIcon fontSize="small" />
                          </div>
                          <div className="flex-1">
                            <p className={`font-bold text-sm ${isSelected ? "text-gray-800" : "text-gray-600"}`}>
                              {car.license}
                            </p>
                            <p className="text-xs text-gray-400">
                              {car.province} {car.type && `• ${car.type}`}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="animate-in zoom-in duration-200">
                              <CheckCircleIcon className="text-[#10B981]" fontSize="small" />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-center">
                    <TruckIcon className="text-gray-300 mb-1" />
                    <p className="text-xs text-gray-400">ไม่มีรถที่ลงทะเบียนไว้</p>
                  </div>
                )}
                {carSelectError && <p className="text-xs text-red-500 mt-1 font-medium">* กรุณาเลือกรถ 1 คัน</p>}
              </div>

              {/* Drivers Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                    <BadgeIcon sx={{ fontSize: 18 }} className="text-gray-400" />
                    คนขับที่ลงทะเบียนไว้
                  </h4>
                  <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {drivers.length ? "เลือกหรือไม่ก็ได้" : "ไม่มีข้อมูล"}
                  </span>
                </div>

                <div
                  className={`
                    relative rounded-xl transition-all
                    ${!selectedCarId && drivers.length > 0 ? "opacity-60 grayscale pointer-events-none" : ""}
                  `}
                >
                  {!selectedCarId && drivers.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                      <span className="bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm font-medium shadow-lg">
                        กรุณาเลือกรถก่อน
                      </span>
                    </div>
                  )}

                  {drivers.length > 0 ? (
                    <div className="grid gap-2 max-h-[160px] overflow-y-auto pr-1">
                      {drivers.map((drv) => {
                        const isSelected = String(selectedDriverId) === String(drv.id);
                        return (
                          <div
                            key={drv.id}
                            onClick={() => toggleDriver(drv.id)}
                            className={`
                                p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3
                                ${isSelected
                                ? "border-orange-500 bg-orange-50 shadow-md"
                                : "border-transparent bg-white shadow-sm hover:border-orange-300 hover:shadow-md"
                              }
                              `}
                          >
                            <div className={`
                                  w-10 h-10 rounded-lg flex items-center justify-center transition-colors
                                  ${isSelected ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-400"}
                               `}>
                              <BadgeIcon fontSize="small" />
                            </div>
                            <div className="flex-1">
                              <p className={`font-bold text-sm ${isSelected ? "text-gray-800" : "text-gray-600"}`}>
                                {[drv?.name, drv?.surname].filter(Boolean).join(" ") || "-"}
                              </p>
                              {drv?.idCard && <p className="text-xs text-gray-400">ID: {drv.idCard}</p>}
                            </div>
                            {isSelected && (
                              <div className="animate-in zoom-in duration-200">
                                <CheckCircleIcon className="text-orange-500" fontSize="small" />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-center">
                      <BadgeIcon className="text-gray-300 mb-1" />
                      <p className="text-xs text-gray-400">ไม่มีคนขับที่ลงทะเบียนไว้</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Right Panel: Form Inputs */}
          <div className="w-full md:w-7/12 p-6 md:p-8 flex flex-col h-full overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800">กรอกข้อมูลการขนส่ง</h3>
              <p className="text-sm text-gray-500">ระบุรายละเอียดเพิ่มเติมสำหรับการขนส่งครั้งนี้</p>
            </div>

            <Stack spacing={3} className="flex-1">
              <TextField
                label="ชื่อการขนส่ง"
                value={shippingName}
                onChange={(e) => setShippingName(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{ sx: { borderRadius: 3 } }}
              />
              <TextField
                label="เลขที่ใบสั่งซื้อ (PO)"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                fullWidth
                InputProps={{ sx: { borderRadius: 3 } }}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="ชื่อคนขับ"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  fullWidth
                  InputProps={{ sx: { borderRadius: 3 } }}
                  helperText={selectedDriverId ? "ดึงจากระบบอัตโนมัติ" : ""}
                />
                <TextField
                  label="เลขตู้ / ทะเบียน"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  fullWidth
                  InputProps={{ sx: { borderRadius: 3 } }}
                  helperText={selectedCarId ? "ดึงจากระบบอัตโนมัติ" : ""}
                />
              </div>

              <TextField
                label="โทรศัพท์"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
                InputProps={{ sx: { borderRadius: 3 } }}
              />

              <TextField
                label="หมายเหตุ"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                placeholder="ระบุรายละเอียดอื่นๆ เพิ่มเติม..."
                InputProps={{ sx: { borderRadius: 3 } }}
                helperText="* ข้อมูลนี้จะแสดงในใบรายการสินค้า"
              />
            </Stack>
          </div>
        </div>
      </DialogContent>

      <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 z-10">
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            fontSize: '1rem'
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          disableElevation
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1.2,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
            }
          }}
        >
          {submitting ? (
            <div className="flex items-center gap-2">
              <CircularProgress size={20} color="inherit" />
              <span>กำลังบันทึก...</span>
            </div>
          ) : (
            "สร้างรายการ"
          )}
        </Button>
      </div>
    </Dialog>
  );
}

function UserList() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openShippingDialog, setOpenShippingDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const q = searchParams.get("search");
    if (q != null) {
      try {
        setSearch(decodeURIComponent(q));
      } catch {
        setSearch(q);
      }
    }
    fetchCustomers();
  }, [searchParams]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerServices.getCustomers("SELLER");
      const data = res?.data?.data || [];
      setCustomers(data);
      return data;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const includesSafe = (a = "", b = "") =>
    String(a || "").toLowerCase().includes(String(b || "").toLowerCase());

  const matchSearch = (customer) => {
    if (!search) return true;
    const keyword = search.toLowerCase();

    // Check name
    const byName = includesSafe(customer.name, keyword);

    // Check cars
    const byCarLicense = (customer.cars || []).some((car) =>
      includesSafe(car?.license, keyword)
    );

    // Check drivers
    const byDriverName = (customer.drivers || []).some((d) => {
      const fullName = [d.name, d.surname].filter(Boolean).join(" ");
      return (
        includesSafe(d.name, keyword) ||
        includesSafe(d.surname, keyword) ||
        includesSafe(fullName, keyword)
      );
    });

    return byName || byCarLicense || byDriverName;
  };

  const filteredData = customers.filter(matchSearch);

  const handleCardClick = (customerId) => {
    const customer = filteredData.find((item) => item.id === customerId);
    if (!customer) return;
    setSelectedCustomer(customer);
    setOpenShippingDialog(true);
  };

  const handleCreateClick = () => {
    navigate(`/sale-customers/create?sale=true`);
  };

  // Re-use logic for creating sale transaction
  const handleShippingSubmit = async (shipping) => {
    if (!selectedCustomer) return;
    const payload = {
      transactionId: uuidv4(),
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.name,
      ...(shipping.carId ? { carId: shipping.carId } : {}),
      ...(shipping.driverId ? { driverId: shipping.driverId } : {}),
      status: "waiting",
      shipping: {
        purchaseOrderNumber: shipping.purchaseOrderNumber || null,
        shippingName: shipping.shippingName || null,
        driverName: shipping.driverName || null,
        vehicleNumber: shipping.vehicleNumber || null,
        phoneNumber: shipping.phoneNumber || null,
        note: shipping.note,
      },
    };

    const response = await salengServices.createSaleTransaction(payload);
    setOpenShippingDialog(false);
    navigate(
      `/sale/checkout?id=${response.data.data.transactionId}&ticket=${response.data.data.billNumber}`
    );
  };

  return (
    <Box p={2}>
      <AppBreadcrumbs />
      <div className="h-[calc(100vh-64px)] bg-gray-50 flex flex-col overflow-hidden">
        {/* Fixed Header */}
        <div className="z-30 bg-white border-b border-gray-200 shadow-sm w-full shrink-0">
          <div className="px-4 py-3 md:px-8 max-w-7xl mx-auto w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Left: Title */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
                  <GroupsIcon fontSize="medium" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800 leading-tight">
                    เลือกลูกค้า (ฝั่งขาย)
                  </h1>
                  <p className="text-xs text-gray-500">
                    เลือกบัญชีเพื่อเปิดตั๋วขายสินค้า
                  </p>
                </div>
              </div>

              {/* Right: Search & Actions */}
              <div className="flex items-center gap-3 flex-1 md:flex-none w-full md:w-auto">
                <div className="relative flex-1 md:w-80 group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#10B981] transition-colors">
                    <SearchIcon />
                  </div>
                  <input
                    type="text"
                    placeholder="ค้นหาชื่อ, ทะเบียนรถ, คนขับ..."
                    className="bg-gray-100 block w-full pl-10 pr-3 py-2.5 rounded-xl border border-transparent text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#10B981] focus:border-transparent sm:text-sm transition-all shadow-inner"
                    value={search}
                    onChange={(e) => setSearch(e.target.value.trimStart())}
                  />
                </div>

                <button className="w-11 h-11 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors border border-gray-200">
                  <FilterListIcon />
                </button>

                <button
                  onClick={handleCreateClick}
                  className="flex items-center gap-2 bg-gradient-to-br from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white py-2.5 px-5 rounded-xl font-medium shadow-md shadow-green-200 active:scale-95 transition-all whitespace-nowrap"
                >
                  <AddIcon fontSize="small" />
                  ลูกค้าใหม่
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="flex-1 p-4 md:p-8 w-full overflow-y-auto">
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <CircularProgress color="success" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                <SearchIcon fontSize="large" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                ไม่พบลูกค้าที่ค้นหา
              </h3>
              <p className="text-gray-500 mt-1">
                ลองใช้คำค้นหาอื่น หรือสร้างลูกค้าใหม่
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6 pb-20">
              {filteredData.map((item) => {
                const isSelected = selectedCustomer?.id === item.id;
                const randomColorClass = getRandomColor(item.id);
                const initial = item.name ? item.name.charAt(0) : "?";

                return (
                  <div
                    key={item.id}
                    onClick={() => handleCardClick(item.id)}
                    className={`
                       group relative bg-white rounded-2xl p-6 cursor-pointer transition-all duration-200 ease-out
                       border flex flex-col h-full
                       ${isSelected
                        ? "border-[#10B981] ring-2 ring-[#10B981] ring-offset-2 shadow-lg z-10"
                        : "border-gray-100 hover:border-[#10B981]/50 hover:shadow-lg hover:-translate-y-1"
                      }
                   `}
                  >
                    {isSelected && (
                      <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-[#10B981] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1 z-20">
                        <CheckCircleIcon sx={{ fontSize: 12 }} />
                        เลือกอยู่
                      </div>
                    )}

                    {/* Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className={`w-14 h-14 rounded-full shrink-0 flex items-center justify-center text-2xl font-bold ${randomColorClass}`}
                      >
                        {initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-gray-800 truncate leading-tight mb-1 group-hover:text-[#10B981] transition-colors">
                          {item.name}
                        </h3>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                          {item.personType === "normal"
                            ? "บุคคลธรรมดา"
                            : "นิติบุคคล"}
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 flex flex-col gap-3">
                      {/* Cars */}
                      {item.cars && item.cars.length > 0 && (
                        <div className="flex flex-col gap-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                            รถที่ลงทะเบียนไว้
                          </p>
                          {item.cars.slice(0, 3).map((car) => (
                            <div
                              key={car.id}
                              className="flex items-center gap-2 text-sm text-gray-600"
                            >
                              <TruckIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                              <span className="truncate flex-1">
                                <span className="font-semibold text-gray-700">
                                  {car.license}
                                </span>
                                {car.province && (
                                  <span className="text-gray-400">
                                    {" "}
                                    ({car.province})
                                  </span>
                                )}
                                {car.type && (
                                  <span className="text-gray-400">
                                    {" "}
                                    - {car.type}
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                          {item.cars.length > 3 && (
                            <span className="text-xs text-gray-400 pl-7">
                              +{item.cars.length - 3} คัน อื่นๆ
                            </span>
                          )}
                        </div>
                      )}

                      {(!item.cars || item.cars.length === 0) && (
                        <div className="flex items-center gap-2 text-sm text-gray-300 italic">
                          <TruckIcon sx={{ fontSize: 18, opacity: 0.3 }} />
                          ไม่มีรถลงทะเบียน
                        </div>
                      )}

                      <div className="h-px bg-gray-50 w-full my-1"></div>

                      {/* Drivers */}
                      {item.drivers && item.drivers.length > 0 ? (
                        <div className="flex flex-col gap-2 mt-auto">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                            คนขับที่ลงทะเบียนไว้
                          </p>
                          {item.drivers.slice(0, 2).map((d) => (
                            <div
                              key={d.id}
                              className="flex items-center gap-2 bg-orange-50/50 rounded-lg p-2 border border-orange-100/50"
                            >
                              <div className="w-6 h-5 rounded bg-orange-100 flex items-center justify-center text-orange-400 shrink-0">
                                <BadgeIcon sx={{ fontSize: 14 }} />
                              </div>
                              <span className="text-sm font-medium text-gray-700 truncate">
                                {d.name} {d.surname}
                              </span>
                            </div>
                          ))}
                          {item.drivers.length > 2 && (
                            <span className="text-xs text-gray-400 text-center">
                              +{item.drivers.length - 2} คน อื่นๆ
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="mt-auto pt-2">
                          <p className="text-[10px] text-gray-400 text-center bg-gray-50 rounded py-1">
                            ไม่พบคนขับ
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Dialog */}
        <ShippingDialog
          open={openShippingDialog}
          onClose={() => setOpenShippingDialog(false)}
          onSubmit={handleShippingSubmit}
          customer={selectedCustomer}
        />
      </div>
    </Box>
  );
}

export default UserList;
